import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { Loader2, Calendar, Users, MapPin, Video, GraduationCap, CreditCard } from "lucide-react";
import { UniHeader, fmtDateRange, fmtSessionRange, usd } from "./_lmsShared";

export default function UniversityEventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ student_email: "", student_name: "", phone: "", notes: "" });
  const [registering, setRegistering] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await universityApi.eventGet(eventId);
        setEvent(r.data?.event || null);
      } catch (e) {
        toast.error(formatApiError(e, "Could not load event"));
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onRegister = async (e) => {
    e.preventDefault();
    if (!form.student_email.trim()) { toast.error("Email required"); return; }
    setRegistering(true);
    try {
      const r = await universityApi.eventRegister(eventId, form);
      setRegistration(r.data?.registration || null);
      if (r.data?.waitlisted) toast.success("You're on the waitlist — we'll email when a seat opens.");
      else if (r.data?.duplicate) toast.info("You're already registered — opening your payment link.");
      else toast.success("Registered! Complete payment to confirm your seat.");
    } catch (err) {
      toast.error(formatApiError(err, "Registration failed"));
    } finally {
      setRegistering(false);
    }
  };

  const onPay = async () => {
    if (!registration) return;
    setCheckoutBusy(true);
    try {
      const r = await universityApi.eventCheckout(eventId, registration.id);
      const url = r.data?.checkout_url;
      if (!url) {
        toast.error("Checkout link unavailable. Please contact admissions.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Opening secure Square checkout…");
    } catch (e) {
      toast.error(formatApiError(e, "Checkout failed"));
    } finally {
      setCheckoutBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#FBF8F2] p-10 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;
  if (!event) return <div className="min-h-screen bg-[#FBF8F2] p-10">Event not found.</div>;

  const Icon = event.delivery_type === "in_person" ? MapPin : Video;
  const isResidency = event.event_type === "clinical_residency";
  const back = isResidency ? "/university/residency" : "/university/ce-calendar";

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title={isResidency ? "Residency" : "CE Event"} back={back} />
      <main className="max-w-4xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="uni-event-detail">
        <article className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-purple-900">
            <Icon className="w-3.5 h-3.5" />{(event.delivery_type || "online_live").replace("_", " ")}
            {event.track && <span className="ml-2 inline-flex items-center text-amber-900">Track: {event.track.replace("_", " ")}</span>}
          </div>
          <h1 className="mt-1 font-display text-3xl text-slate-900" data-testid="uni-event-title">{event.title}</h1>
          <div className="mt-2 text-sm text-slate-500" data-testid="event-detail-when">{fmtSessionRange(event)}</div>
          {event.location && <div className="mt-1 text-sm text-slate-500">Location: {event.location}</div>}
          {event.instructor_name && <div className="mt-1 text-sm text-slate-500">Instructor: {event.instructor_name}</div>}
          <p className="mt-4 text-[15px] leading-relaxed text-slate-800 whitespace-pre-wrap">{event.description}</p>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Tile label="Price" value={usd(event.price_usd)} />
            <Tile label="Clock Hours" value={Number(event.metadata?.clock_hours || event.ce_hours || event.residency_hours || 0)} />
            <Tile label="CE Hours" value={Number(event.ce_hours || 0)} />
            <Tile label="Delivery Format" value={(event.delivery_type || "online_live").replace("_", " ")} />
            <Tile label="Seats Remaining" value={event.seats_remaining == null ? "Open" : event.seats_remaining} />
            <Tile label="Certificate Eligible" value={event.metadata?.certificate_eligible === false ? "No" : "Yes"} />
            {event.residency_hours > 0 && <Tile label="Residency hours" value={event.residency_hours} />}
            {event.clinical_hours > 0 && <Tile label="Clinical hours" value={event.clinical_hours} />}
            {event.includes_kit && <Tile label="Includes" value="Clinical Kit" />}
          </div>
        </article>

        <aside className="rounded-2xl border border-slate-200 bg-white p-6">
          {!registration ? (
            <form onSubmit={onRegister} className="space-y-3" data-testid="uni-event-register-form">
              <div className="text-xs uppercase tracking-wide text-slate-500">Register</div>
              <input className="w-full rounded-xl border border-slate-200 px-3 h-10 text-sm" type="email" required placeholder="Your email" value={form.student_email} onChange={(e) => set("student_email", e.target.value)} data-testid="uni-event-email" />
              <input className="w-full rounded-xl border border-slate-200 px-3 h-10 text-sm" placeholder="Full name" value={form.student_name} onChange={(e) => set("student_name", e.target.value)} data-testid="uni-event-name" />
              <input className="w-full rounded-xl border border-slate-200 px-3 h-10 text-sm" placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              <button type="submit" disabled={registering} className="w-full rounded-xl bg-purple-900 hover:bg-purple-800 text-white font-medium h-10 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="uni-event-register-btn">
                {registering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}Register
              </button>
              <p className="text-[11px] text-slate-500">Seat is held for 24 hours pending payment. Secure checkout via Square.</p>
            </form>
          ) : (
            <div className="space-y-3" data-testid="uni-event-registered">
              <div className="text-xs uppercase tracking-wide text-emerald-700">Registered</div>
              <div className="text-sm text-slate-700">
                Status: <strong>{registration.status.replace("_", " ")}</strong>
                {registration.status === "waitlist" && registration.waitlist_position > 0 && (
                  <> · Waitlist position #{registration.waitlist_position}</>
                )}
              </div>
              {registration.status === "pending_payment" && (
                <button type="button" disabled={checkoutBusy} onClick={onPay} className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white font-medium h-10 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="uni-event-pay-btn">
                  {checkoutBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}Pay {usd(event.price_usd)} via Square
                </button>
              )}
              {registration.status === "paid" && <div className="text-sm text-emerald-700">Payment confirmed. We&apos;ll email you details before the session.</div>}
            </div>
          )}
        </aside>
      </main>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-slate-900 font-medium">{value}</div>
    </div>
  );
}
